/**
 * STITCH-2-ASSIGN-AND-EXPERIENCE-V1 -- the Lead quick-view VIEW MODEL (pure).
 *
 * ONE truth for the role-aware Lead-360 quick view (the benchmark call-pop
 * pattern rendered in AdoraCare's own language): every hosting surface builds
 * the SAME `LeadQuickViewModel` from the read it ALREADY holds --
 *   - workspace surfaces (Leads pages, Workboard, Today follow-ups) build it
 *     from the role-projected `Lead360Input` record (the same read the
 *     canonical full page renders; role-restricted fields are structurally
 *     ABSENT there, so this model can never resurrect them);
 *   - the manager leakage queue + queue-health drill build it from the
 *     PHI-safe `SlaBreachItemView` (the manager tiers are NOT in the leads
 *     workspace audience, so their drawer content comes from the read they
 *     are already served -- no second permission path, no new read).
 * SEAM (recorded in the run record 1(e)): this is the compact quick view
 * sharing the canonical view model, not a full-page embed; "Open full view"
 * links to /dashboard/leads/:id for roles the canonical page admits.
 *
 * PHI LAW: no phone, no clinical field exists on either input; attribution
 * renders as the HUMAN source label only (never UTM/click-ids). The audited
 * contact reveal lives ONLY on the canonical full page.
 */

import type { Lead360Input, LeadOwnershipView } from '@/dashboard/owner/lead-360-view';
import {
  resolveLastContactAttempt,
  type LastContactAttempt,
} from '@/dashboard/leads/last-contact-attempt';
import { formatLeadRef } from '@/dashboard/owner/leads-object-view';
import { formatSourceLabel } from '@/dashboard/owner/leakage-funnel-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import {
  STAGE_NEXT_ACTION,
  STAGE_OUTCOME_LABEL,
} from '@/dashboard/leads/lead-lifecycle-rail-view';
import { resolveLeadRecordCurrentTruth } from '@/dashboard/record/current-truth';
import type { SlaBreachItemView } from '@/dashboard/sla-leakage/sla-leakage-view';
import { displayNameOf, formatLeakageAge, itemActionHint } from '@/dashboard/sla-leakage/sla-leakage-view';

export interface QuickViewActivityEntry {
  readonly label: string;
  readonly whenLabel: string | null;
}

export interface LeadQuickViewModel {
  /** Numeric lead id for the action endpoints (claim/assign/stage). */
  readonly leadId: number | null;
  readonly safeLeadId: string | null;
  /** Person title: the lead's name, else the honest safe ref. */
  readonly title: string;
  readonly clinicLabel: string | null;
  /** Raw canon stage token (drives the SCOPE-5 stage strip), or null. */
  readonly stage: string | null;
  readonly stageLabel: string | null;
  /** THE current-truth next lawful action (same contract as the page). */
  readonly nextAction: string | null;
  /** Owner plane for the action strip; null when the surface cannot say. */
  readonly ownership: LeadOwnershipView | null;
  /** HUMAN source label only (attribution law). */
  readonly sourceLabel: string | null;
  readonly capturedAtLabel: string | null;
  /** Plain-language waiting/age line (leakage surfaces). */
  readonly waitingLabel: string | null;
  /** Latest activity entries, newest first, humanized. */
  readonly activity: readonly QuickViewActivityEntry[];
  /** One-line appointment summary when the record carries one. */
  readonly appointmentLabel: string | null;
  /** True when the lead is linked to a patient record (chip only). */
  readonly patientLinked: boolean;
  /** RETURNING SIGNAL: true when an UNLINKED lead's phone matches an
   *  existing patient (record.patientLink.status === 'possible_match').
   *  Chip-only; id/UMR live on the record, never a phone. Surfaces whose
   *  backing read carries no patientLink stay false (named seam). */
  readonly existingPatientMatch: boolean;
  // --- RECORD-360-WORKSPACE-V1 (V1-P5) current-truth fields -------------
  // Workspace-record-backed drawers resolve THE current-truth contract
  // (record/current-truth.ts) -- the SAME resolver the full page renders --
  // so drawer value === page value by construction (rider R-02). The
  // non-workspace builders (leakage / tile drills) carry honest nulls: their
  // PHI-safe backing reads ship no appointment cards (the S-B named seam).
  /** Current conversion state kind, or null on non-workspace reads. */
  readonly conversionKind: string | null;
  readonly conversionLabel: string | null;
  /** Current payment state kind, or null on non-workspace reads. */
  readonly paymentKind: string | null;
  readonly paymentLabel: string | null;
  /** Follow-up-required why line; null when none / not on this read. */
  readonly followUpLabel: string | null;
  /** Identity (golden-record link) label; null on non-workspace reads. */
  readonly identityLabel: string | null;
  /** Historical reversal labels (superseded contrary outcomes). */
  readonly historicalWarningLabels: readonly string[];
  /**
   * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 (Mission 10): the last
   * recorded contact attempt (the SAME projection the full record renders,
   * last-contact-attempt.ts). Null on non-workspace reads (their backing
   * wires carry no disposition events -- the S-B named seam) and when no
   * attempt is recorded yet (the honest empty state).
   */
  readonly lastAttempt: LastContactAttempt | null;
  // STABLE IDS (same-record parity proof: drawer === page on the RECORD,
  // not merely on similar label strings).
  readonly currentAppointmentId: number | null;
  readonly caseSheetId: number | null;
  readonly patientRecordId: string | null;
}

const MAX_ACTIVITY_ENTRIES = 4;

function safeTimestamp(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  try {
    return formatTimestampIst(iso);
  } catch {
    return null;
  }
}

/**
 * Build the model from a role-projected workspace record (ONE truth).
 *
 * RECORD-360-WORKSPACE-V1 (V1-P5): the state fields (next action, current
 * appointment, conversion, payment, follow-up, identity) resolve through
 * THE current-truth contract -- the SAME `resolveLeadRecordCurrentTruth`
 * the full page renders -- so the drawer can never disagree with the page
 * (rider R-02 / founder E4 / E-360-4: the stage-only next action that read
 * "Make first contact within SLA" beside a fulfilled appointment is
 * structurally gone). `nowMs` is injected (module purity law).
 */
export function buildQuickViewFromLead360(
  record: Lead360Input,
  nowMs: number,
): LeadQuickViewModel {
  const truth = resolveLeadRecordCurrentTruth(record, nowMs);
  const leadIdNum = Number(record.safeLeadId);
  const stage = record.stage ?? null;
  const events = record.events ?? [];
  const activity: QuickViewActivityEntry[] = events
    .slice(0, MAX_ACTIVITY_ENTRIES)
    .map((e) => ({
      label: e.summary ?? e.eventType.replace(/_/g, ' '),
      whenLabel: safeTimestamp(e.occurredAt),
    }));
  const appt = truth.currentAppointment;
  return {
    leadId: Number.isSafeInteger(leadIdNum) && leadIdNum > 0 ? leadIdNum : null,
    safeLeadId: record.safeLeadId,
    title:
      typeof record.leadName === 'string' && record.leadName.trim() !== ''
        ? record.leadName
        : formatLeadRef(record.safeLeadId),
    clinicLabel: record.clinicLabel ?? null,
    stage,
    stageLabel: stage !== null ? STAGE_OUTCOME_LABEL[stage] : null,
    nextAction: truth.nextLawfulAction.label,
    ownership: record.ownerAssignment ?? null,
    sourceLabel:
      typeof record.sourceNormalized === 'string' &&
      record.sourceNormalized.trim() !== ''
        ? formatSourceLabel(record.sourceNormalized)
        : null,
    capturedAtLabel: safeTimestamp(record.createdAt),
    waitingLabel: null,
    activity,
    // The CURRENT appointment (the contract's selection law) -- never
    // cards[0] (the soonest-by-time card, which misstated the current one).
    appointmentLabel:
      appt.kind !== 'none'
        ? `${safeTimestamp(appt.appointmentTime ?? '') ?? 'Time not recorded'} - ${appt.statusLabel ?? appt.storedStatus ?? 'Unknown'}`
        : null,
    // The REAL link truth (leads.patient_id via record.goldenRecordLink) --
    // the old revenueLinked mapping could never light (the read model
    // hardcodes revenueLinked 'not_available'; run-record finding).
    patientLinked: record.goldenRecordLink?.status === 'linked',
    existingPatientMatch: record.goldenRecordLink?.status === 'possible_match',
    conversionKind: truth.currentConversionState.kind,
    conversionLabel: truth.currentConversionState.label,
    paymentKind: truth.currentPaymentState.kind,
    paymentLabel: truth.currentPaymentState.label,
    followUpLabel: truth.currentFollowUpState.whyLabel,
    identityLabel: truth.identityState.label,
    historicalWarningLabels: truth.historicalWarnings.labels,
    lastAttempt: resolveLastContactAttempt(record),
    currentAppointmentId: truth.stableIds.appointmentId,
    caseSheetId: truth.stableIds.caseSheetId,
    patientRecordId: truth.stableIds.patientRecordId,
  };
}

/** Build the model from a PHI-safe leakage item (manager surfaces). */
export function buildQuickViewFromLeakageItem(
  item: SlaBreachItemView,
): LeadQuickViewModel {
  const leadIdNum = item.safeLeadId !== null ? Number(item.safeLeadId) : NaN;
  const assigned =
    typeof item.assignedLabel === 'string' && item.assignedLabel.trim() !== '';
  const ownership: LeadOwnershipView | null =
    item.refKind === 'lead'
      ? {
          assigned,
          isMine: false,
          ownerLabel: assigned ? item.assignedLabel : null,
          escalated: !assigned && item.severity === 'red',
        }
      : null;
  return {
    leadId: Number.isSafeInteger(leadIdNum) && leadIdNum > 0 ? leadIdNum : null,
    safeLeadId: item.safeLeadId,
    title: displayNameOf(item),
    clinicLabel: item.clinicLabel,
    stage: null,
    stageLabel: item.stageLabel,
    nextAction: itemActionHint(item),
    ownership,
    sourceLabel: null,
    capturedAtLabel: null,
    waitingLabel: `Waiting ${formatLeakageAge(item.ageMinutes)}`,
    activity: item.timeline.slice(0, MAX_ACTIVITY_ENTRIES).map((e) => ({
      label: e.label,
      whenLabel: safeTimestamp(e.occurredAtIso),
    })),
    appointmentLabel: null,
    patientLinked: false,
    existingPatientMatch: false,
    // S-B named seam (re-affirmed by V1-P5): the leakage read carries no
    // appointment cards / plan facts / patient link, so the current-truth
    // fields stay honestly null here -- never fabricated.
    conversionKind: null,
    conversionLabel: null,
    paymentKind: null,
    paymentLabel: null,
    followUpLabel: null,
    identityLabel: null,
    historicalWarningLabels: [],
    lastAttempt: null,
    currentAppointmentId: null,
    caseSheetId: null,
    patientRecordId: null,
  };
}

/** Build the model from a queue-health drill item (manager surfaces;
 *  STITCH-2 SCOPE 6 -- same PHI posture as the leakage item). */
export function buildQuickViewFromTileItem(item: {
  readonly leadId: number;
  readonly safeLeadId: string;
  readonly displayName: string;
  readonly clinicLabel?: string | null;
  readonly ageMinutes: number | null;
  readonly stage: string | null;
  readonly ownerLabel: string | null;
  readonly assigned: boolean | null;
}): LeadQuickViewModel {
  const stage =
    item.stage !== null && item.stage in STAGE_OUTCOME_LABEL
      ? (item.stage as keyof typeof STAGE_OUTCOME_LABEL)
      : null;
  return {
    leadId: item.leadId,
    safeLeadId: item.safeLeadId,
    title: item.displayName.trim() !== '' ? item.displayName : 'Unnamed lead',
    clinicLabel: item.clinicLabel ?? null,
    stage,
    stageLabel: stage !== null ? STAGE_OUTCOME_LABEL[stage] : null,
    nextAction: stage !== null ? STAGE_NEXT_ACTION[stage] : null,
    ownership:
      item.assigned === null
        ? null
        : {
            assigned: item.assigned,
            isMine: false,
            ownerLabel: item.ownerLabel,
            escalated: false,
          },
    sourceLabel: null,
    capturedAtLabel: null,
    waitingLabel:
      item.ageMinutes !== null ? `Waiting ${formatLeakageAge(item.ageMinutes)}` : null,
    activity: [],
    appointmentLabel: null,
    patientLinked: false,
    existingPatientMatch: false,
    // S-B named seam (re-affirmed by V1-P5): the tile-drill read carries no
    // appointment cards / plan facts / patient link -- honest nulls only.
    conversionKind: null,
    conversionLabel: null,
    paymentKind: null,
    paymentLabel: null,
    followUpLabel: null,
    identityLabel: null,
    historicalWarningLabels: [],
    lastAttempt: null,
    currentAppointmentId: null,
    caseSheetId: null,
    patientRecordId: null,
  };
}

/** The canonical full-page href, or null when the id is unusable. */
export function quickViewFullHref(model: LeadQuickViewModel): string | null {
  return model.safeLeadId !== null && model.safeLeadId.trim() !== ''
    ? `/dashboard/leads/${model.safeLeadId}`
    : null;
}
